export enum sectionTypes {
  SEPARATOR = 'Separator',
  CONTENT = 'Content',
}

/**
 * Limits applied to reference files attached to AI widgets.
 * Enforced by the widget editor, the Composer upload API, and the backend AI endpoints,
 * so they must be read from here rather than redefined per application.
 */
export const AI_WIDGET_FILE_LIMITS = {
  /** Maximum number of files a single AI widget may reference */
  MAX_COUNT: 5,
  /** Maximum size of one attached file, in bytes */
  MAX_FILE_SIZE_BYTES: 20 * 1024 * 1024,
  /** Maximum combined size of all files attached to one widget, in bytes */
  MAX_TOTAL_SIZE_BYTES: 50 * 1024 * 1024,
} as const

/**
 * Supported document file extensions for Playbook AI (compatible with OpenAI vector store retrieval).
 */
export const PLAYBOOK_AI_SUPPORTED_DOCUMENT_EXTENSIONS = [
  'doc',
  'docx',
  'html',
  'json',
  'md',
  'pdf',
  'pptx',
  'tex',
  'txt',
] as const

/**
 * Supported image file extensions for Playbook AI (multimodal vision input).
 */
export const PLAYBOOK_AI_SUPPORTED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'] as const

/**
 * All allowed file extensions for Playbook AI attachments (generation and chat).
 * Restricts allowed files to vector-store-compatible documents and supported static image formats.
 */
export const PLAYBOOK_AI_SUPPORTED_FILE_EXTENSIONS = [
  ...PLAYBOOK_AI_SUPPORTED_DOCUMENT_EXTENSIONS,
  ...PLAYBOOK_AI_SUPPORTED_IMAGE_EXTENSIONS,
] as const

/**
 * File extensions supported for OpenAI vector store retrieval.
 * Images are deliberately excluded: they are passed via direct multimodal input (input_image)
 * and fail with a 400 error if uploaded to OpenAI vector stores.
 */
export const VECTOR_STORE_SUPPORTED_FILE_EXTENSIONS = PLAYBOOK_AI_SUPPORTED_DOCUMENT_EXTENSIONS

/**
 * File extensions accepted as AI widget reference files.
 *
 * Attachments are only ever consumed through OpenAI file search, so this list is
 * restricted to the formats OpenAI vector stores can ingest. Spreadsheets and images
 * are deliberately excluded: they upload successfully but fail at ingestion time.
 */
export const AI_WIDGET_SUPPORTED_FILE_EXTENSIONS = PLAYBOOK_AI_SUPPORTED_DOCUMENT_EXTENSIONS

/**
 * Check whether a file has an extension allowed for Playbook AI.
 */
export function isPlaybookAiSupportedExtension(fileName: string): boolean {
  return isSupportedExtension(fileName, PLAYBOOK_AI_SUPPORTED_FILE_EXTENSIONS)
}

/**
 * Check whether a file has an extension supported for OpenAI vector store retrieval.
 */
export function isVectorStoreSupportedExtension(fileName: string): boolean {
  return isSupportedExtension(fileName, VECTOR_STORE_SUPPORTED_FILE_EXTENSIONS)
}

/**
 * Check whether a file has an extension matching supported image types.
 */
export function isPlaybookAiImageExtension(fileName: string): boolean {
  return isSupportedExtension(fileName, PLAYBOOK_AI_SUPPORTED_IMAGE_EXTENSIONS)
}

/**
 * Check whether a file has an extension included in the list of supported extensions.
 * @param fileName The name of the file to check.
 * @param supportedExtensions The list of supported file extensions.
 * @returns True if the file has a supported extension, false otherwise.
 */
function isSupportedExtension(fileName: string, supportedExtensions: readonly string[]): boolean {
  const dotIndex = fileName.lastIndexOf('.')
  if (dotIndex < 0) return false
  const extension = fileName.slice(dotIndex + 1).toLowerCase()
  return supportedExtensions.includes(extension)
}
